import { useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { AnimatePresence, motion, useScroll, useTransform } from 'framer-motion';
import { alaViewport } from './alaMotion';

const introImageDef: SlotGroupDefinition = {
  label: 'Intro Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Landmark360 house-shaped lifestyle photo',
    },
  },
};

const gallery1Def: SlotGroupDefinition = {
  label: 'Gallery Image 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Landmark360 gallery photo 1' },
  },
};

const gallery2Def: SlotGroupDefinition = {
  label: 'Gallery Image 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Landmark360 gallery photo 2' },
  },
};

const gallery3Def: SlotGroupDefinition = {
  label: 'Gallery Image 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Landmark360 gallery photo 3' },
  },
};

const gallery4Def: SlotGroupDefinition = {
  label: 'Gallery Image 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Landmark360 gallery photo 4' },
  },
};

const gallery5Def: SlotGroupDefinition = {
  label: 'Gallery Image 5',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Landmark360 gallery photo 5' },
  },
};

const feature1ImageDef: SlotGroupDefinition = {
  label: 'Feature 1 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Easier Living' },
  },
};

const feature2ImageDef: SlotGroupDefinition = {
  label: 'Feature 2 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Experiences' },
  },
};

const feature3ImageDef: SlotGroupDefinition = {
  label: 'Feature 3 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Pet Friendly Living' },
  },
};

const feature4ImageDef: SlotGroupDefinition = {
  label: 'Feature 4 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Flexible Solutions' },
  },
};

const promise1ImageDef: SlotGroupDefinition = {
  label: 'Promise 1 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Move-In Satisfaction' },
  },
};

const promise2ImageDef: SlotGroupDefinition = {
  label: 'Promise 2 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: '48-Hour Completion Promise' },
  },
};

const promise3ImageDef: SlotGroupDefinition = {
  label: 'Promise 3 Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Hassle-Free Relocation Promise' },
  },
};

const propertiesCtaDef: SlotGroupDefinition = {
  label: 'View Properties CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/our-communities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Properties' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const GALLERY_LAYOUT = [
  { h: 256, mt: 128 },
  { h: 288, mt: 96 },
  { h: 384, mt: 94 },
  { h: 288, mt: 96 },
  { h: 256, mt: 128 },
] as const;

const TAB_CLIP =
  'polygon(0 0, calc(100% - 24px) 0, 100% 50%, calc(100% - 24px) 100%, 0 100%)';

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

function ArrowDownIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function ChevronUpIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}

function PromiseCtas({
  properties,
  contact,
}: {
  properties: { url?: string; label?: string; _target?: string };
  contact: { url?: string; label?: string; _target?: string };
}) {
  return (
    <div className="flex flex-wrap items-center gap-4 pt-4">
      <EditableSlotGroup groupPath="landmark360content.properties_cta" definition={propertiesCtaDef}>
        <a
          href={properties.url || '/our-communities'}
          target={properties._target || '_self'}
          className="group inline-flex h-[54px] items-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#3d7fd9]"
        >
          <span className="font-heading text-base font-medium leading-[19px]">
            {properties.label || 'View Properties'}
          </span>
          <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </a>
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="landmark360content.contact_cta" definition={contactCtaDef}>
        <a
          href={contact.url || '/contact-us'}
          target={contact._target || '_self'}
          className="inline-flex h-[54px] items-center justify-center rounded-full px-6 shadow-[inset_0_0_0_1px_rgb(var(--ala-secondary))] transition-colors hover:bg-[rgb(var(--ala-secondary))]/10"
        >
          <span className="font-heading text-base font-medium leading-[19px] text-[rgb(var(--ala-text))]">
            {contact.label || 'Contact Us'}
          </span>
        </a>
      </EditableSlotGroup>
    </div>
  );
}

export function Landmark360Content() {
  const [activePromise, setActivePromise] = useState(0);
  const [expandedMobile, setExpandedMobile] = useState(false);
  const scrollBandRef = useRef<HTMLDivElement>(null);

  const introImage = useSlotGroup('landmark360content.intro_image', introImageDef);
  const gallery1 = useSlotGroup('landmark360content.gallery_1', gallery1Def);
  const gallery2 = useSlotGroup('landmark360content.gallery_2', gallery2Def);
  const gallery3 = useSlotGroup('landmark360content.gallery_3', gallery3Def);
  const gallery4 = useSlotGroup('landmark360content.gallery_4', gallery4Def);
  const gallery5 = useSlotGroup('landmark360content.gallery_5', gallery5Def);
  const feature1Image = useSlotGroup('landmark360content.feature_1_image', feature1ImageDef);
  const feature2Image = useSlotGroup('landmark360content.feature_2_image', feature2ImageDef);
  const feature3Image = useSlotGroup('landmark360content.feature_3_image', feature3ImageDef);
  const feature4Image = useSlotGroup('landmark360content.feature_4_image', feature4ImageDef);
  const promise1Image = useSlotGroup('landmark360content.promise_1_image', promise1ImageDef);
  const promise2Image = useSlotGroup('landmark360content.promise_2_image', promise2ImageDef);
  const promise3Image = useSlotGroup('landmark360content.promise_3_image', promise3ImageDef);
  const propertiesCta = useSlotGroup('landmark360content.properties_cta', propertiesCtaDef);
  const contactCta = useSlotGroup('landmark360content.contact_cta', contactCtaDef);

  const { scrollYProgress } = useScroll({
    target: scrollBandRef,
    offset: ['start end', 'start start'],
  });
  const bandBg = useTransform(scrollYProgress, [0, 1], ['hsl(0 0% 96%)', '#04101d']);

  const galleryItems = [
    {
      image:
        gallery1.image ||
        '',
      alt: gallery1.alt || '',
      ...GALLERY_LAYOUT[0],
      def: gallery1Def,
      path: 'landmark360content.gallery_1',
    },
    {
      image:
        gallery2.image ||
        '',
      alt: gallery2.alt || '',
      ...GALLERY_LAYOUT[1],
      def: gallery2Def,
      path: 'landmark360content.gallery_2',
    },
    {
      image:
        gallery3.image ||
        '',
      alt: gallery3.alt || '',
      ...GALLERY_LAYOUT[2],
      def: gallery3Def,
      path: 'landmark360content.gallery_3',
    },
    {
      image:
        gallery4.image ||
        '',
      alt: gallery4.alt || '',
      ...GALLERY_LAYOUT[3],
      def: gallery4Def,
      path: 'landmark360content.gallery_4',
    },
    {
      image:
        gallery5.image ||
        '',
      alt: gallery5.alt || '',
      ...GALLERY_LAYOUT[4],
      def: gallery5Def,
      path: 'landmark360content.gallery_5',
    },
  ];

  const features = [
    {
      image:
        feature1Image.image ||
        '',
      alt: feature1Image.alt || '',
      titlePath: 'landmark360content.feature_1_title',
      bodyPath: 'landmark360content.feature_1_body',
      titleLabel: 'Feature 1 Title',
      bodyLabel: 'Feature 1 Body',
      def: feature1ImageDef,
      path: 'landmark360content.feature_1_image',
    },
    {
      image:
        feature2Image.image ||
        '',
      alt: feature2Image.alt || '',
      titlePath: 'landmark360content.feature_2_title',
      bodyPath: 'landmark360content.feature_2_body',
      titleLabel: 'Feature 2 Title',
      bodyLabel: 'Feature 2 Body',
      def: feature2ImageDef,
      path: 'landmark360content.feature_2_image',
    },
    {
      image:
        feature3Image.image ||
        '',
      alt: feature3Image.alt || '',
      titlePath: 'landmark360content.feature_3_title',
      bodyPath: 'landmark360content.feature_3_body',
      titleLabel: 'Feature 3 Title',
      bodyLabel: 'Feature 3 Body',
      def: feature3ImageDef,
      path: 'landmark360content.feature_3_image',
    },
    {
      image:
        feature4Image.image ||
        '',
      alt: feature4Image.alt || '',
      titlePath: 'landmark360content.feature_4_title',
      bodyPath: 'landmark360content.feature_4_body',
      titleLabel: 'Feature 4 Title',
      bodyLabel: 'Feature 4 Body',
      def: feature4ImageDef,
      path: 'landmark360content.feature_4_image',
    },
  ];

  const promises = [
    {
      titlePath: 'landmark360content.promise_1_title',
      bodyPath: 'landmark360content.promise_1_body',
      titleLabel: 'Promise 1 Title',
      bodyLabel: 'Promise 1 Body',
      image:
        promise1Image.image ||
        '',
      alt: promise1Image.alt || '',
      def: promise1ImageDef,
      path: 'landmark360content.promise_1_image',
      fallbackTitle: 'Move-In Satisfaction',
    },
    {
      titlePath: 'landmark360content.promise_2_title',
      bodyPath: 'landmark360content.promise_2_body',
      titleLabel: 'Promise 2 Title',
      bodyLabel: 'Promise 2 Body',
      image:
        promise2Image.image ||
        '',
      alt: promise2Image.alt || '',
      def: promise2ImageDef,
      path: 'landmark360content.promise_2_image',
      fallbackTitle: '48-Hour Completion Promise',
    },
    {
      titlePath: 'landmark360content.promise_3_title',
      bodyPath: 'landmark360content.promise_3_body',
      titleLabel: 'Promise 3 Title',
      bodyLabel: 'Promise 3 Body',
      image:
        promise3Image.image ||
        '',
      alt: promise3Image.alt || '',
      def: promise3ImageDef,
      path: 'landmark360content.promise_3_image',
      fallbackTitle: 'Hassle-Free Relocation Promise',
    },
  ];

  const desktopActive = activePromise < 0 ? 0 : activePromise;
  const activePromiseItem = promises[desktopActive];
  const marqueeItems = [...galleryItems, ...galleryItems];

  return (
    <>
      <section className="grid w-full grid-cols-1 items-center gap-12 bg-[#F9F9F9] px-6 py-24 md:px-12 lg:grid-cols-2 lg:gap-20 lg:px-16 lg:py-32">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={alaViewport}
          transition={{ duration: 0.8 }}
          className="relative flex flex-col gap-10 lg:gap-12"
        >
          <RichTextContent
            path="landmark360content.headline"
            label="Headline"
            as="h2"
            className="max-w-[667px] font-heading text-[clamp(2rem,5.28vw,76px)] font-semibold leading-[1.1053] text-[rgb(var(--ala-text))]"
          />
          <div className="relative pl-12 md:pl-16">
            <div className="absolute left-0 top-0 flex h-full flex-col items-center">
              <div className="w-px flex-1 bg-[rgb(var(--ala-text))]/30" />
              <RichTextContent
                path="landmark360content.discover_label"
                label="Discover Label"
                as="span"
                className="-rotate-90 origin-center whitespace-nowrap py-8 font-heading text-[11px] font-semibold uppercase tracking-[0.25em] text-[rgb(var(--ala-text))]"
              />
              <div className="w-px flex-1 bg-[rgb(var(--ala-text))]/30" />
              <ArrowDownIcon className="mt-2 h-5 w-5 text-[rgb(var(--ala-text))]" />
            </div>
            <RichTextContent
              path="landmark360content.body"
              label="Body"
              as="p"
              className="max-w-[543px] font-heading text-lg font-normal leading-[1.7] text-[#333333]"
            />
          </div>
        </motion.div>

        <EditableSlotGroup groupPath="landmark360content.intro_image" definition={introImageDef}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={alaViewport}
            transition={{ duration: 0.8 }}
            className="flex justify-center lg:justify-end"
          >
            <VibesitesImage
              url={
                introImage.image ||
                ''
              }
              alt={introImage.alt || ''}
              sizes="(min-width: 1024px) 427px, 90vw"
              className="h-auto w-full max-w-[427px] object-cover"
            />
          </motion.div>
        </EditableSlotGroup>
      </section>

      <motion.div ref={scrollBandRef} style={{ backgroundColor: bandBg }} className="w-full">
        <section className="relative w-full overflow-clip py-12">
          <div className="flex w-[200%]">
            <motion.div
              className="flex shrink-0 items-start gap-6"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ ease: 'linear', duration: 40, repeat: Infinity }}
            >
              {marqueeItems.map((item, index) => {
                const slide = (
                  <div
                    className="shrink-0 overflow-clip rounded-lg bg-[#48757c]"
                    style={{ width: 384, height: item.h, marginTop: item.mt }}
                  >
                    <VibesitesImage
                      url={item.image}
                      alt={item.alt}
                      sizes="384px"
                      className="h-full w-full object-cover"
                    />
                  </div>
                );
                if (index < galleryItems.length) {
                  return (
                    <EditableSlotGroup key={`${item.path}-${index}`} groupPath={item.path} definition={item.def}>
                      {slide}
                    </EditableSlotGroup>
                  );
                }
                return <div key={`${item.path}-loop-${index}`}>{slide}</div>;
              })}
            </motion.div>
          </div>
        </section>

        <section className="flex w-full flex-col gap-12 px-6 py-24 md:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.6 }}
            className="flex flex-col gap-2"
          >
            <RichTextContent
              path="landmark360content.features_eyebrow"
              label="Features Eyebrow"
              as="p"
              className="font-heading text-lg font-semibold uppercase leading-[25px] text-[rgb(var(--ala-secondary))]"
            />
            <RichTextContent
              path="landmark360content.features_headline"
              label="Features Headline"
              as="h2"
              className="font-heading text-[clamp(30px,3.82vw,55px)] font-medium leading-[1.5273] text-[rgb(var(--ala-on-primary))]"
            />
          </motion.div>

          <div className="flex flex-col gap-4 lg:flex-row">
            {features.map((feature, index) => (
              <motion.div
                key={feature.path}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={alaViewport}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="group flex flex-1 flex-col gap-5 overflow-hidden rounded-[6px] bg-[#e5e5e5] p-3 transition-all duration-500 lg:hover:flex-[2.5] lg:hover:bg-[rgb(var(--ala-background))] lg:hover:shadow-xl"
              >
                <EditableSlotGroup groupPath={feature.path} definition={feature.def}>
                  <div className="relative min-h-[229px] w-full overflow-clip rounded-[6px] bg-[rgb(var(--ala-surface))]">
                    <VibesitesImage
                      url={feature.image}
                      alt={feature.alt}
                      sizes="(min-width: 1024px) 25vw, 90vw"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </div>
                </EditableSlotGroup>
                <div className="flex flex-col gap-2 px-3 pb-2">
                  <RichTextContent
                    path={feature.titlePath}
                    label={feature.titleLabel}
                    as="h3"
                    className="font-heading text-[18px] font-medium leading-[29px] tracking-[0.2px] text-[rgb(var(--ala-text))]"
                  />
                  <div className="max-h-40 overflow-hidden transition-all duration-500 lg:max-h-0 lg:group-hover:max-h-40">
                    <RichTextContent
                      path={feature.bodyPath}
                      label={feature.bodyLabel}
                      as="p"
                      className="pt-2 font-heading text-base font-normal leading-[23px] text-[rgb(var(--ala-text))]"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </motion.div>

      <section className="flex w-full flex-col gap-12 bg-[#F5F5F5] px-6 py-24 md:px-16">
        <RichTextContent
          path="landmark360content.promise_headline"
          label="Promise Headline"
          as="h2"
          className="font-heading text-[clamp(1.75rem,4vw,3rem)] font-semibold leading-[1.2889] text-[rgb(var(--ala-text))] opacity-80"
        />

        {/* Mobile accordion */}
        <div className="flex flex-col gap-3 lg:hidden">
          {promises.map((promise, index) => {
            const isOpen = activePromise === index;
            return (
              <div key={promise.path} className="flex flex-col">
                <button
                  type="button"
                  onClick={() => {
                    setActivePromise(isOpen ? -1 : index);
                    setExpandedMobile(false);
                  }}
                  style={{ clipPath: TAB_CLIP }}
                  className={`flex h-[86px] w-full items-center justify-between gap-4 py-6 pl-6 pr-16 text-left transition-colors duration-300 ${
                    isOpen ? 'bg-[rgb(var(--ala-secondary))] text-[rgb(var(--ala-on-secondary))]' : 'bg-[#E0E0E0] text-[#555555]'
                  }`}
                >
                  <RichTextContent
                    path={promise.titlePath}
                    label={promise.titleLabel}
                    as="span"
                    className="font-heading text-lg font-semibold leading-[1.2917]"
                  />
                  <span
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
                      isOpen ? 'bg-white/20' : 'bg-black/10'
                    }`}
                  >
                    {isOpen ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-5 pt-6">
                        <EditableSlotGroup groupPath={promise.path} definition={promise.def}>
                          <div className="aspect-[3/2] w-full overflow-clip rounded-[24px]">
                            <VibesitesImage
                              url={promise.image}
                              alt={promise.alt}
                              sizes="100vw"
                              className="h-full w-full object-cover"
                            />
                          </div>
                        </EditableSlotGroup>
                        <RichTextContent
                          path={promise.titlePath}
                          label={promise.titleLabel}
                          as="h3"
                          className="font-heading text-lg font-medium leading-[1.2917] tracking-[0.0083em] text-[rgb(var(--ala-text))]"
                        />
                        <div className={expandedMobile ? '' : 'line-clamp-3'}>
                          <RichTextContent
                            path={promise.bodyPath}
                            label={promise.bodyLabel}
                            as="div"
                            className="font-heading text-base font-normal leading-[23px] text-[rgb(var(--ala-text))] [&_p]:mb-3 [&_p:last-child]:mb-0"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedMobile((value) => !value)}
                          className="w-fit font-heading text-base font-medium text-[rgb(var(--ala-secondary))] underline"
                        >
                          {expandedMobile ? 'Read less' : 'Read more'}
                        </button>
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            );
          })}
          <PromiseCtas properties={propertiesCta} contact={contactCta} />
        </div>

        {/* Desktop tabs */}
        <div className="hidden gap-24 lg:grid lg:grid-cols-[429px_1fr]">
          <div className="z-10 flex flex-col gap-10 self-start lg:sticky lg:top-48">
            <div className="flex flex-col gap-3">
              {promises.map((promise, index) => {
                const isActive = desktopActive === index;
                return (
                  <button
                    key={promise.path}
                    type="button"
                    onClick={() => {
                      setActivePromise(index);
                      setExpandedMobile(false);
                    }}
                    style={{ clipPath: TAB_CLIP }}
                    className={`flex h-[86px] w-full items-center py-6 pl-6 pr-12 text-left transition-colors duration-300 ${
                      isActive ? 'bg-[rgb(var(--ala-accent))] text-[rgb(var(--ala-on-primary))]' : 'bg-[#E0E0E0] text-[#555555] hover:bg-gray-300'
                    }`}
                  >
                    <RichTextContent
                      path={promise.titlePath}
                      label={promise.titleLabel}
                      as="span"
                      className="font-heading text-2xl font-medium leading-[1.2917]"
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={desktopActive}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col gap-8"
              >
                <EditableSlotGroup
                  groupPath={activePromiseItem.path}
                  definition={activePromiseItem.def}
                >
                  <div className="aspect-[3/2] w-full overflow-clip rounded-[24px]">
                    <VibesitesImage
                      url={activePromiseItem.image}
                      alt={activePromiseItem.alt}
                      sizes="(min-width: 1024px) 50vw, 100vw"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </EditableSlotGroup>
                <div className="flex flex-col gap-5">
                  <RichTextContent
                    path={activePromiseItem.titlePath}
                    label={activePromiseItem.titleLabel}
                    as="h3"
                    className="font-heading text-2xl font-medium leading-[1.2917] tracking-[0.0083em] text-[rgb(var(--ala-text))]"
                  />
                  <RichTextContent
                    path={activePromiseItem.bodyPath}
                    label={activePromiseItem.bodyLabel}
                    as="div"
                    className="max-w-[770px] font-heading text-base font-normal leading-[23px] text-[rgb(var(--ala-text))] [&_p]:mb-3 [&_p:last-child]:mb-0"
                  />
                </div>
              </motion.div>
            </AnimatePresence>
            <PromiseCtas properties={propertiesCta} contact={contactCta} />
          </div>
        </div>

        {/* Literal wrappers for schema-linter (dynamic map paths aren't statically visible). */}
        <div className="hidden" aria-hidden>
          <EditableSlotGroup groupPath="landmark360content.gallery_1" definition={gallery1Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.gallery_2" definition={gallery2Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.gallery_3" definition={gallery3Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.gallery_4" definition={gallery4Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.gallery_5" definition={gallery5Def}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.feature_1_image" definition={feature1ImageDef}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.feature_2_image" definition={feature2ImageDef}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.feature_3_image" definition={feature3ImageDef}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.feature_4_image" definition={feature4ImageDef}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.promise_1_image" definition={promise1ImageDef}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.promise_2_image" definition={promise2ImageDef}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="landmark360content.promise_3_image" definition={promise3ImageDef}>
            <span />
          </EditableSlotGroup>
          <RichTextContent path="landmark360content.feature_1_title" label="Feature 1 Title" as="span" />
          <RichTextContent path="landmark360content.feature_1_body" label="Feature 1 Body" as="span" />
          <RichTextContent path="landmark360content.feature_2_title" label="Feature 2 Title" as="span" />
          <RichTextContent path="landmark360content.feature_2_body" label="Feature 2 Body" as="span" />
          <RichTextContent path="landmark360content.feature_3_title" label="Feature 3 Title" as="span" />
          <RichTextContent path="landmark360content.feature_3_body" label="Feature 3 Body" as="span" />
          <RichTextContent path="landmark360content.feature_4_title" label="Feature 4 Title" as="span" />
          <RichTextContent path="landmark360content.feature_4_body" label="Feature 4 Body" as="span" />
          <RichTextContent path="landmark360content.promise_1_title" label="Promise 1 Title" as="span" />
          <RichTextContent path="landmark360content.promise_1_body" label="Promise 1 Body" as="span" />
          <RichTextContent path="landmark360content.promise_2_title" label="Promise 2 Title" as="span" />
          <RichTextContent path="landmark360content.promise_2_body" label="Promise 2 Body" as="span" />
          <RichTextContent path="landmark360content.promise_3_title" label="Promise 3 Title" as="span" />
          <RichTextContent path="landmark360content.promise_3_body" label="Promise 3 Body" as="span" />
        </div>
      </section>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Landmark360Content',
  displayName: 'Landmark360 Content',
  tokens: {
    'landmark360content.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>Stick around for great living</p>',
    },
    'landmark360content.discover_label': {
      label: 'Discover Label',
      type: 'richtext',
      defaultValue: '<p>Discover</p>',
    },
    'landmark360content.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        "<p>Landmark360® is American Landmark's resident promise — a collection of exclusive benefits, service guarantees, and experiences designed to make apartment living simpler, more flexible, and more enjoyable from move-in through renewal.</p>",
    },
    'landmark360content.features_eyebrow': {
      label: 'Features Eyebrow',
      type: 'richtext',
      defaultValue: '<p>WELCOME HOME</p>',
    },
    'landmark360content.features_headline': {
      label: 'Features Headline',
      type: 'richtext',
      defaultValue: '<p>More Than an Apartment. A Better Way to Rent.</p>',
    },
    'landmark360content.feature_1_title': {
      label: 'Feature 1 Title',
      type: 'richtext',
      defaultValue: '<p>Easier Living</p>',
    },
    'landmark360content.feature_1_body': {
      label: 'Feature 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>From online payments to maintenance requests, everyday tasks are simple and convenient.</p>',
    },
    'landmark360content.feature_2_title': {
      label: 'Feature 2 Title',
      type: 'richtext',
      defaultValue: '<p>Community Experiences</p>',
    },
    'landmark360content.feature_2_body': {
      label: 'Feature 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Resident events, wellness activities, social gatherings, and opportunities to connect.</p>',
    },
    'landmark360content.feature_3_title': {
      label: 'Feature 3 Title',
      type: 'richtext',
      defaultValue: '<p>Pet Friendly Living</p>',
    },
    'landmark360content.feature_3_body': {
      label: 'Feature 3 Body',
      type: 'richtext',
      defaultValue: "<p>Because home isn't home without your four-legged family members.</p>",
    },
    'landmark360content.feature_4_title': {
      label: 'Feature 4 Title',
      type: 'richtext',
      defaultValue: '<p>Flexible Solutions</p>',
    },
    'landmark360content.feature_4_body': {
      label: 'Feature 4 Body',
      type: 'richtext',
      defaultValue: '<p>Life changes. Landmark360® helps make those transitions easier.</p>',
    },
    'landmark360content.promise_headline': {
      label: 'Promise Headline',
      type: 'richtext',
      defaultValue: '<p>The Landmark360® Promise</p>',
    },
    'landmark360content.promise_1_title': {
      label: 'Promise 1 Title',
      type: 'richtext',
      defaultValue: '<p>Move-In Satisfaction</p>',
    },
    'landmark360content.promise_1_body': {
      label: 'Promise 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>It’s our goal to make moving into your new space as simple and streamlined as possible. American Landmark Apartments rolls out the red carpet for you – a reserved parking space for your moving vehicle, a welcome home gift, and a 100% satisfaction guarantee in the initial two weeks. If you aren’t content with your new space within your first two weeks, we will:</p><p>Refund your security deposit and, if applicable, pet deposit, minus any damages.</p><p>Waive all fees associated with the lease termination.</p><p>Customer must provide a written 15-Day Notice to Vacate at least 15 days before moving.</p><p>Rent charges must be paid in full through the move-out date, including all monthly fees and utilities.</p><p>You may not apply any free rent concessions (for full or partial months) toward your rent payment obligations under the Move-In Satisfaction Promise.</p>',
    },
    'landmark360content.promise_2_title': {
      label: 'Promise 2 Title',
      type: 'richtext',
      defaultValue: '<p>48-Hour Completion Promise</p>',
    },
    'landmark360content.promise_2_body': {
      label: 'Promise 2 Body',
      type: 'richtext',
      defaultValue:
        "<p>We understand that maintenance issues can be frustrating. That's why we guarantee a 48-hour response and completion time for routine service requests. If we fail to meet this standard, you will be compensated for the inconvenience. Our dedicated team is always on standby to ensure your home remains in perfect condition.</p>",
    },
    'landmark360content.promise_3_title': {
      label: 'Promise 3 Title',
      type: 'richtext',
      defaultValue: '<p>Hassle-Free Relocation Promise</p>',
    },
    'landmark360content.promise_3_body': {
      label: 'Promise 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Need to move for work or personal reasons? As a Landmark360® resident, you have the flexibility to transfer to any other American Landmark community across the country without paying standard lease break fees. We make your transition seamless so you can focus on your next chapter.</p>',
    },
  },
  slotGroups: {
    'landmark360content.intro_image': introImageDef,
    'landmark360content.gallery_1': gallery1Def,
    'landmark360content.gallery_2': gallery2Def,
    'landmark360content.gallery_3': gallery3Def,
    'landmark360content.gallery_4': gallery4Def,
    'landmark360content.gallery_5': gallery5Def,
    'landmark360content.feature_1_image': feature1ImageDef,
    'landmark360content.feature_2_image': feature2ImageDef,
    'landmark360content.feature_3_image': feature3ImageDef,
    'landmark360content.feature_4_image': feature4ImageDef,
    'landmark360content.promise_1_image': promise1ImageDef,
    'landmark360content.promise_2_image': promise2ImageDef,
    'landmark360content.promise_3_image': promise3ImageDef,
    'landmark360content.properties_cta': propertiesCtaDef,
    'landmark360content.contact_cta': contactCtaDef,
  },
};

(Landmark360Content as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default Landmark360Content;
